import React from "react";
import {
  Container, // Switched to react-bootstrap Container
  Button,
  Card,
  Modal,
} from "react-bootstrap";
import moment from "moment";
import { get } from "lodash";
import core from "../../../vendors/core-api";
import ProSourceForm from "../../../components/ProSourceForm";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import SmartReservationForm from "../../../partials/reservations/SmartReservationForm";
import {
  fetchReservableTablesData,
  buildTimeSlots,
  getIsTableFullyBooked,
  getTableUniqueExperience as getTableUniqueExperienceUtil,
  getPrunedSelectionsAfterPartySizeChange,
} from "../../../partials/reservations/reservationSlots";
import {
  createReservation,
  seedCustomAmount,
} from "../../../partials/reservations/reservationsApi";
import { tryParse } from "../../../helpers";
import { getCalendarEventProps } from "../../../helpers/reservations";
// --- Hardcoded constants to remove dependencies ---
const RESERVATION_COLOR = "#673ab7"; // Using the purple from the modal
const DB_DATE_FORMAT = "YYYY-MM-DD"; // This is now only used for formatting the string
const MySwal = withReactContent(Swal);
// --- Rewritten date functions to remove Moment.js ---
// These functions are used by the header, not the modal logic.
/**
 * Formats a Date object or date string into YYYY-MM-DD format.
 * @param {Date | string} date The date to format.
 * @returns {string} Formatted date string.
 */
const formatDateForInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Gets the 7-day week array centered on the given date.
 * @param {Date | string} date The central date.
 * @returns {Array<object>}
 */
const getCurrentWeek = (date) => {
  const startWeek = new Date(date);
  // Set to 3 days before the selected date
  startWeek.setDate(startWeek.getDate() - 3);

  const daysArr = [];

  for (let i = 0; i < 7; i++) {
    const day = new Date(startWeek);
    day.setDate(day.getDate() + i);

    daysArr.push({
      date: day,
      day: day.toLocaleDateString("en-US", { weekday: "short" }), // "Mon"
      number: day.toLocaleDateString("en-US", { day: "2-digit" }), // "05"
    });
  }
  return daysArr;
};
// --- End of date function replacements ---

const INITIAL_VALUES = {
  // Use moment for initial state as the modal logic still uses it
  reservation_date: moment().add(1, "days").format(DB_DATE_FORMAT),
  party_size: 4,
  experience: null,
  table: null,
  reservation_time: null,
  firstName: "",
  lastName: "",
  email: "",
  contact: "",
  dietary: [],
  otherDietaryNote: "",
  celebration: [],
  otherCelebrationNote: "",
  allowOverBooking: true,
  overrideReservableDays: true,
  additionals: [],
};

class ReservationsCalendarHeader extends ProSourceForm {
  constructor(props) {
    super(props);
    this.api = core("pu").get();
    this.state = {
      showModal: false,
      isAdding: false,
      isDeleting: false,
      allTables: [],
      reservableTables: [],
      allReservableTables: [],
      values: { ...INITIAL_VALUES },
      errors: {},
      touched: {},
      calendarEvents: [],
      showDatepickerModal: false,
      monthlyReservations: [], // To store reservations for the month view
      calendarRef: React.createRef(),
      selected_reservation: null,
      calendarRefOverview: React.createRef(),
      selected_date: this.props.selected_date,
    };
    this.state.withPaymentLink = false;

    this.handleEventDrop = this.handleEventDrop.bind(this);
    this.handleReservationSelect = this.handleReservationSelect.bind(this);
  }

  handleShowModal = (dateToShow) => {
    const dateToUse = dateToShow || this.props.selected_date;

    this.fetchAllTables();
    this.setState(
      (prevState) => ({
        showModal: true,
        values: { ...prevState.values, reservation_date: dateToUse },
      }),
      () => {
        this.fetchReservableTables();
        this.refetchReservationByDate(dateToUse);
      },
    );
  };

  handleCloseModal = (callback) => {
    this.setState(
      {
        showModal: false,
        selected_reservation: null,
        calendarEvents: [], // Clear events on close
        values: { ...INITIAL_VALUES },
      },
      () => {
        // If a callback is provided, execute it after the state has been updated.
        this.fetchAllTables();
        if (typeof callback === "function") callback();
      },
    );
  };

  handleShowDatepickerModal = () => {
    setTimeout(() => {
      this.state.calendarRefOverview.current.getApi().updateSize();
    }, 100);
    this.setState({ showDatepickerModal: true });
  };

  handleCloseDatepickerModal = () => {
    this.setState({ showDatepickerModal: false });
  };

  componentDidUpdate(prevProps, prevState) {
    // If the reservation date in the modal has changed...
    if (
      prevState.values.reservation_date !== this.state.values.reservation_date
    ) {
      const calendarApi = this.state.calendarRef.current?.getApi();

      // Create a date object in the local timezone from the YYYY-MM-DD string
      // Appending T00:00:00 ensures it's parsed as local time, not UTC.
      const newDate = new Date(this.state.values.reservation_date);

      if (calendarApi) {
        const currentCalendarDate = calendarApi.getDate();

        // Only call gotoDate if the month or year is different
        if (
          currentCalendarDate.getFullYear() !== newDate.getFullYear() ||
          currentCalendarDate.getMonth() !== newDate.getMonth()
        ) {
          calendarApi.gotoDate(newDate);
        }

        // We don't need calendarApi.render().
        // The state change will trigger a React re-render,
        // and the new `dayCellClassNames` prop will handle styling.
      }
    }

    // Scroll to selected date when datepicker modal opens
    if (this.state.showDatepickerModal && !prevState.showDatepickerModal) {
      setTimeout(() => {
        const selectedDateElement = document.querySelector(
          ".selected-reservation-date",
        );
        if (selectedDateElement) {
          selectedDateElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 200); // Small delay to ensure calendar has rendered
    }
  }

  dayHeaderContent = (arg) => {
    const dayReservations = this.state.monthlyReservations.filter((r) =>
      moment(r.order_date).isSame(arg.date, "day"),
    );
    const totalPax = dayReservations.reduce((acc, res) => {
      const guestCount = res.order_details
        ? res.order_details
            .filter((detail) => detail.item?.type === "reservation_experience")
            .reduce((subAcc, cur) => subAcc + (cur.qty || 0), 0)
        : res.guest_count || 0;
      return acc + guestCount;
    }, 0);
    return (
      <div>
        <div>{arg.dayNumberText}</div>
        <div style={{ fontSize: "0.7em" }}>
          {dayReservations.length === 0
            ? ""
            : `${dayReservations.length} (${totalPax})`}
        </div>
      </div>
    );
  };

  fetchAllTables = async () => {
    const { store_id } = this.props;

    if (!store_id) return;
    try {
      const { data } = await this.api.get(
        `/branded-website/stores/${store_id}/reservable-tables`,
      );

      this.setState({ allTables: data.store_tables || [] });
    } catch (error) {
      console.error("Failed to fetch all tables:", error);
    }
  };

  fetchReservableTables = async () => {
    const { store_id } = this.props;
    const {
      reservation_date,
      party_size,
      overrideReservableDays,
      overrideTableCapacity,
    } = this.state.values;

    this.setState({ loading: true });
    try {
      const { reservableTables, allReservableTables } =
        await fetchReservableTablesData({
          api: this.api,
          store_id,
          reservation_date,
          party_size,
          override_reservable_days: overrideReservableDays !== false,
          override_table_capacity: overrideTableCapacity,
        });
      if (
        getIsTableFullyBooked(
          this.state.values.table,
          this.state.values.reservation_time,
          reservableTables,
        )
      ) {
        this.setFieldValue("allowOverBooking", true);
      }
      this.setState({ reservableTables, allReservableTables });
      return { reservableTables, allReservableTables };
    } catch (error) {
      console.error("Failed to fetch reservable tables:", error);
      this.setState({ reservableTables: [], allReservableTables: [] });
      return { reservableTables: [], allReservableTables: [] };
    } finally {
      this.setState({ loading: false });
    }
  };

  fetchCalendarReservations = async (fetchInfo, successCallback) => {
    const { store_id } = this.props;
    const start = moment(fetchInfo.startStr).format(DB_DATE_FORMAT);
    const end = moment(fetchInfo.endStr).format(DB_DATE_FORMAT);

    try {
      const { data } = await this.api.get(
        `/reservation/store/${store_id}/date-range?startDate=${start}&endDate=${end}`,
      );

      const allReservationsForMonth = Object.values(data)
        .flat()
        .filter((res) => !res.status?.toLowerCase().includes("cancelled"));

      this.setState({ monthlyReservations: allReservationsForMonth });

      const events = allReservationsForMonth.map(getCalendarEventProps);

      this.setState({ calendarEvents: events });
      if (successCallback) successCallback(events);
    } catch (error) {
      console.error("Failed to fetch calendar reservations:", error);
    }
  };
  handleEventClick = (clickInfo) => {
    const reservation = clickInfo.event.extendedProps.reservation;
    if (!reservation) return;

    const table = reservation.order_details.find(
      (od) => od.item.type === "reservation",
    );
    const experience = reservation.order_details.find(
      (od) => od.item.type === "reservation_experience",
    );
    const originalNotes = tryParse(reservation.notes) || {};
    const additionals = (reservation.order_details || [])
      .filter((od) => od.item && od.item.type === "reservation_addons")
      .map((od) => ({
        label: od.item?.name || od.name,
        amount: Number(od.item?.price ?? od.price) || 0,
      }));
    const reservation_date = reservation.order_date;
    this.setState(
      {
        selected_reservation: reservation,
        withPaymentLink: reservation.payment?.status === "pending",
        selected_date: reservation_date,
        values: {
          allowOverBooking: true,
          reservation_date,
          party_size: experience ? experience.qty : 1,
          experience: experience ? experience.item._id : null,
          table: table ? table.item._id : null,
          reservation_time: reservation.order_time,
          firstName: reservation.guest_customer.first_name,
          lastName: reservation.guest_customer.last_name,
          email: reservation.guest_customer.email,
          contact: reservation?.guest_customer?.contact_number ?? "",
          dietary: originalNotes.dietary || [],
          celebration: originalNotes.celebration || [],
          additionals,
          // Mirrored onto the Order regardless of v1/v2 mode (see
          // helpers/reservation.js's reservation_notes mirror) — seeded here
          // so re-opening an already-overridden reservation for editing
          // doesn't silently revert its custom price/override on save.
          customAmount: seedCustomAmount(reservation.reservation_notes),
          overrideTableCapacity: Boolean(
            reservation.reservation_notes?.party_size_override,
          ),
        },
      },
      () => {
        this.handleShowModal(reservation_date);
      },
    );
  };

  handleReservationSelect = (reservation) => {
    if (!reservation) return;

    const table = reservation.order_details.find(
      (od) => od.item && od.item.type === "reservation",
    );
    const experience = reservation.order_details.find(
      (od) => od.item && od.item.type === "reservation_experience",
    );
    const originalNotes = tryParse(reservation.notes) || {};
    const additionals = (reservation.order_details || [])
      .filter((od) => od.item && od.item.type === "reservation_addons")
      .map((od) => ({
        label: od.item?.name || od.name,
        amount: Number(od.item?.price ?? od.price) || 0,
      }));
    const reservation_date = reservation.order_date;
    this.setState(
      {
        selected_reservation: reservation,
        withPaymentLink: reservation.payment?.status === "pending",
        selected_date: reservation_date,
        values: {
          overrideReservableDays: true,
          reservation_date,
          party_size: experience ? experience.qty : 1,
          experience: experience ? experience.item._id : null,
          table: table ? table.item._id : null,
          reservation_time: reservation.order_time,
          firstName: reservation.guest_customer.first_name,
          lastName: reservation.guest_customer.last_name,
          email: reservation.guest_customer.email,
          contact: reservation?.guest_customer?.contact_number ?? "",
          dietary: originalNotes.dietary || [],
          celebration: originalNotes.celebration || [],
          additionals,
          // Mirrored onto the Order regardless of v1/v2 mode (see
          // helpers/reservation.js's reservation_notes mirror) — seeded here
          // so re-opening an already-overridden reservation for editing
          // doesn't silently revert its custom price/override on save.
          customAmount: seedCustomAmount(reservation.reservation_notes),
          overrideTableCapacity: Boolean(
            reservation.reservation_notes?.party_size_override,
          ),
        },
      },
      () => {
        this.handleShowModal(reservation_date);
      },
    );
  };

  handleCancelReservation = async () => {
    if (this.state.selected_reservation) {
      try {
        await this.props.onStatusChange(
          "cancelled",
          this.state.values.table,
          this.state.selected_reservation,
        );
        const { reservation_date } = this.state.values;
        // Reset the form to its initial state and close the modal
        this.setState({
          values: { ...INITIAL_VALUES, reservation_date },
          selected_reservation: null,
          // showModal: false,
        });
        this.refetchReservationByDate(reservation_date);
        this.props.onRefreshList();
      } catch (error) {
        // The user likely hit "cancel" on the confirmation dialog, or an API error occurred.
        // The error is already shown by SweetAlert, so we don't need to do anything here.
      }
    }
  };

  handleEventDrop = async (dropInfo) => {
    const { event, revert } = dropInfo;
    const reservation = event.extendedProps.reservation;
    const newDate = moment(event.start).format(DB_DATE_FORMAT);
    const oldDate = moment(reservation.order_date).format("LL");
    const formattedNewDate = moment(newDate).format("LL");

    const result = await MySwal.fire({
      title: "Confirm Move",
      html: `Are you sure you want to move this reservation from <b>${oldDate}</b> to <b>${formattedNewDate}</b>?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: RESERVATION_COLOR,
      cancelButtonColor: "#F64E60",
      confirmButtonText: "Yes, move it!",
    });

    if (result.isConfirmed) {
      const experience = reservation.order_details.find(
        (od) => od.item.type === "reservation_experience",
      );
      const table = reservation.order_details.find(
        (od) => od.item.type === "reservation",
      );

      const payload = {
        reservation: reservation,
        order_date: newDate,
        order_time: reservation.order_time,
        table_id: table?.item?._id || table?.item,
        experience_id: experience?.item?._id || experience?.item,
        party_size: experience?.qty,
        additionals: (reservation.order_details || [])
          .filter((od) => od.item && od.item.type === "reservation_addons")
          .map((od) => ({
            label: od.item?.name || od.name,
            amount: Number(od.item?.price ?? od.price) || 0,
          })),
      };

      try {
        await this.props.onEditReservationSubmit(payload, null, false);
        if (this.props.onRefreshList) {
          this.props.onRefreshList();
        }
        // Successfully moved, no need to revert.
        // The calendar will refetch events.
      } catch (error) {
        console.error("Failed to update reservation on drop:", error);
        revert(); // Revert on API failure.
      }
    } else {
      revert(); // Revert if the user cancels the confirmation.
    }
  };
  handleDateChange = (e) => {
    const newDate = e.target.value;
    const isEditMode = !!this.state.selected_reservation;

    if (isEditMode) {
      // Edit mode: only update the date, preserve all other selections
      this.setState(
        (prevState) => ({
          values: {
            ...prevState.values,
            reservation_date: newDate,
          },
        }),
        () => {
          this.fetchReservableTables();
        },
      );
    } else {
      // Create mode: reset all selections for the new date
      this.setState(
        {
          values: {
            ...INITIAL_VALUES,
            reservation_date: newDate,
          },
          selected_reservation: null,
        },
        () => {
          this.fetchReservableTables();
        },
      );
    }
  };

  handlePartySizeChange = (amount) => {
    const { uniqueExperiences, values } = this.state;
    const selectedExperienceDetails = uniqueExperiences?.find(
      (exp) => exp._id === values?.experience,
    );
    if (selectedExperienceDetails?.isExclusive && amount > 0) {
      return;
    }
    const currentSize = this.state.values.party_size;
    const newSize = Math.max(1, currentSize + amount);
    this.setState(
      (prevState) => ({
        values: {
          ...prevState.values,
          party_size: newSize,
        },
      }),
      async () => {
        const fetchedTables = await this.fetchReservableTables();
        this.pruneSelectionsInvalidatedByPartySize(fetchedTables);
      },
    );
  };

  // After a party-size refetch, keep the previously selected time/table/experience
  // unless they no longer exist in the freshly fetched availability data.
  // Takes the freshly fetched tables as an argument rather than reading them
  // off this.state, since setState is not guaranteed to have flushed yet.
  // Shared with ReservationsConsole via getPrunedSelectionsAfterPartySizeChange.
  pruneSelectionsInvalidatedByPartySize = ({
    reservableTables = [],
    allReservableTables = [],
  } = {}) => {
    const { values } = this.state;
    const nextSelections = getPrunedSelectionsAfterPartySizeChange({
      reservableTables,
      allReservableTables,
      values,
    });

    if (!nextSelections) return;

    this.setState((prevState) => ({
      values: { ...prevState.values, ...nextSelections },
    }));
  };
  refetchReservationByDate = async (reservation_date) => {
    await this.fetchCalendarReservations({
      startStr: moment(reservation_date).startOf("month").format(),
      endStr: moment(reservation_date).endOf("month").format(),
    });
  };
  handleCreateReservation = async () => {
    const { store_id, onEditReservationSubmit, onRefreshList } = this.props;
    const {
      reservation_date,
      party_size,
      experience,
      reservation_time,
      firstName,
      lastName,
      email,
      contact,
      table,
      dietary,
      celebration,
      allowOverBooking,
      overrideReservableDays,
      additionals = [],
      customAmount,
      overrideTableCapacity,
      payInPerson,
    } = this.state.values;

    const normalizedContact = (contact || "").trim();

    this.setState({ isAdding: true });

    if (this.state.selected_reservation) {
      // Editing existing reservation
      const payload = {
        notes: {
          dietary: dietary,
          celebration: celebration,
        },
        reservation: this.state.selected_reservation,
        order_date: reservation_date,
        order_time: reservation_time,
        table_id: table,
        experience_id: experience,
        party_size,
        firstName,
        lastName,
        email,
        contact: normalizedContact,
        allowOverBooking: allowOverBooking !== false,
        overrideReservableDays: overrideReservableDays !== false,
        additionals,
        customAmount,
        overrideTableCapacity,
        payInPerson,
      };

      await onEditReservationSubmit(
        payload,
        () => {
          this.setState(
            {
              isAdding: false,
              values: { ...INITIAL_VALUES, reservation_date },
              selected_reservation: null,
            },
            async () => {
              this.fetchReservableTables();
              await this.refetchReservationByDate(reservation_date);

              const modalCalendarApi = this.state.calendarRef.current?.getApi();
              if (modalCalendarApi) modalCalendarApi.refetchEvents();
            },
          );
          if (onRefreshList) {
            onRefreshList();
          }
        }, // Pass a function to be called when done
        false, // Do not close modal
      );
      this.setState({});
    } else {
      // This is for creating a new reservation
      try {
        await createReservation(this.api, store_id, {
          firstName,
          lastName,
          email,
          contact: normalizedContact,
          tableId: table,
          experienceId: experience,
          partySize: party_size,
          reservationDate: reservation_date,
          reservationTime: reservation_time,
          notes: {
            dietary: dietary,
            celebration: celebration,
          },
          withPaymentLink: this.state.withPaymentLink, // Assuming you want to send a confirmation email for admin bookings as well
          allow_overbooking: allowOverBooking !== false,
          override_reservable_days: overrideReservableDays !== false,
          additionals,
          // v2-only — stripped by reservationsApi when the flag is off.
          custom_amount:
            customAmount !== "" && customAmount !== undefined
              ? Number(customAmount)
              : undefined,
          override_table_capacity: overrideTableCapacity,
        });

        // Await the fetch to ensure data is fresh before re-rendering

        MySwal.fire({
          icon: "success",
          title: "Reservation Added!",
          timer: 1500,
          showConfirmButton: false,
        });

        // Instead of closing, reset to Step 1
        this.setState(
          {
            values: { ...INITIAL_VALUES, reservation_date },
            selected_reservation: null,
          },
          async () => {
            this.fetchReservableTables();
            await this.refetchReservationByDate(reservation_date);
            const modalCalendarApi = this.state.calendarRef.current?.getApi();
            if (modalCalendarApi) modalCalendarApi.refetchEvents();
          },
        );

        if (onRefreshList) {
          onRefreshList();
        }
      } catch (error) {
        console.error("Failed to create reservation:", error);
      } finally {
        this.setState({ isAdding: false });
      }
    }

    const calendarApi = this.state.calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.refetchEvents();
    }
    // Loading state is now handled within the if/else blocks.
  };

  handleAddAdditional = (item) => {
    const current = this.state.values.additionals || [];
    this.setFieldValue("additionals", [...current, item]);
  };

  handleRemoveAdditional = (index) => {
    const { selected_reservation, values } = this.state;
    const isPending =
      selected_reservation?.payment?.status === "pending" ||
      selected_reservation?.payment?.status === "failed";
    const isPaidStatus = selected_reservation?.payment?.status === "paid";
    const paidAmount =
      isPending || !isPaidStatus
        ? 0
        : Number(selected_reservation?.payment?.amount || 0);
    if (paidAmount > 0) {
      const experience = (selected_reservation?.order_details || []).find(
        (od) => od.item && od.item.type === "reservation_experience",
      );
      const experienceSubtotal =
        (experience?.price || 0) * (values.party_size || experience?.qty || 1);
      let remainingPaid = Math.max(0, paidAmount - experienceSubtotal);
      const additionals = values.additionals || [];
      for (let i = 0; i < additionals.length; i++) {
        const itemAmount = Number(additionals[i].amount) || 0;
        const isItemPaid = remainingPaid >= itemAmount && itemAmount > 0;
        if (isItemPaid) {
          remainingPaid -= itemAmount;
        }
        if (i === index && isItemPaid) {
          return;
        }
      }
    }
    const current = this.state.values.additionals || [];
    this.setFieldValue(
      "additionals",
      current.filter((_, i) => i !== index),
    );
  };

  handlePillClick = (field, value) => {
    if (field === "table") {
      const currentTable = this.state.values.table;
      const newTable = currentTable === value._id ? null : value._id;
      this.setFieldValue("table", newTable);
      this.setFieldValue("experience", null);
      return;
    } else {
      this.setFieldValue(
        field,
        this.state.values[field] === value ? null : value,
      );
    }
    // Default toggle for other fields
  };

  handleDietaryPillClick = (itemName) => {
    const { values } = this.state;
    const currentItems = [...(values.dietary || [])];

    const existingItemIndex = currentItems.findIndex(
      (item) => item.label.toLowerCase() === itemName.toLowerCase(),
    );

    let newItems;
    if (existingItemIndex > -1) {
      newItems = currentItems.filter(
        (item) => item.label.toLowerCase() !== itemName.toLowerCase(),
      );
    } else {
      newItems = [...currentItems, { label: itemName, count: 1 }];
    }
    this.setFieldValue("dietary", newItems);
  };

  handleUpdateDietaryCount = (itemName, newCount) => {
    const { values } = this.state;
    let newItems;
    if (newCount < 1) {
      // If count is less than 1, remove the item.
      newItems = (values.dietary || []).filter(
        (item) => item.label.toLowerCase() !== itemName.toLowerCase(),
      );
    } else {
      // Otherwise, update the count of the existing item.
      newItems = (values.dietary || []).map((item) =>
        item.label.toLowerCase() === itemName.toLowerCase()
          ? { ...item, count: newCount }
          : item,
      );
    }
    this.setFieldValue("dietary", newItems);
  };

  handleAddCustomDietaryItem = () => {
    const { values } = this.state;
    const itemName = (values.otherDietaryNote || "").trim();
    if (!itemName) return;

    const itemExists = (values.dietary || []).some(
      (item) => item.label.toLowerCase() === itemName.toLowerCase(),
    );

    if (itemExists) {
      this.setFieldValue("otherDietaryNote", "");
      return;
    }

    const newItems = [...(values.dietary || []), { label: itemName, count: 1 }];
    this.setFieldValue("dietary", newItems);
    this.setFieldValue("otherDietaryNote", "");
  };

  handlewithPaymentLinkChange = (e) =>
    this.setState({ withPaymentLink: e.target.checked });

  handleResendEmail = async () => {
    const { selected_reservation } = this.state;
    if (!selected_reservation) return;

    this.setState({ isAdding: true });
    try {
      await this.api.post({
        url: `/reservation/${selected_reservation._id}/resend-payment-link`,
      });
      MySwal.fire({
        toast: true,
        icon: "success",
        title: "Payment link resent!",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to resend payment link:", error);
      MySwal.fire({
        icon: "error",
        title: "Error",
        text: "Could not resend the payment link. Please try again.",
      });
    } finally {
      this.setState({ isAdding: false });
    }
  };

  handleCelebrationPillClick = (itemName) => {
    const { values } = this.state;
    const currentItems = [...(values.celebration || [])];
    const existingItemIndex = currentItems.findIndex(
      (item) => item.label.toLowerCase() === itemName.toLowerCase(),
    );

    let newItems;
    if (existingItemIndex > -1) {
      newItems = currentItems.filter(
        (item) => item.label.toLowerCase() !== itemName.toLowerCase(),
      );
    } else {
      newItems = [...currentItems, { label: itemName, count: 1 }];
    }

    this.setFieldValue("celebration", newItems);
  };

  handleAddCustomCelebrationItem = () => {
    const { values } = this.state;
    const itemName = (values.otherCelebrationNote || "").trim();
    if (!itemName) return;

    const itemExists = (values.celebration || []).some(
      (item) => item.label.toLowerCase() === itemName.toLowerCase(),
    );

    if (itemExists) {
      this.setFieldValue("otherCelebrationNote", "");
      return;
    }

    const newItems = [
      ...(values.celebration || []),
      { label: itemName, count: 1 },
    ];
    this.setFieldValue("celebration", newItems);
    this.setFieldValue("otherCelebrationNote", "");
  };
  handleExclusiveExperiencePillClick = (experienceId) => {
    this.setFieldValue("experience", experienceId);
    this.setState((prevState) => ({
      values: { ...prevState.values, party_size: 1 },
    }));
  };
  getModalMode = () => {
    const { selected_reservation } = this.state;
    if (!selected_reservation) return "create";
    return "update";
  };
  getTableUniqueExperience = (tableId) =>
    getTableUniqueExperienceUtil({
      tableId,
      allReservableTables: this.state.allReservableTables,
      reservationTime: this.state.values.reservation_time,
    });

  render() {
    const { selected_date, onCalendarBtnClick, onDateInputChange, onDayClick } =
      this.props;
    const {
      showModal,
      isAdding,
      isDeleting,
      reservableTables,
      allReservableTables,
      values,
      showDatepickerModal,
      withPaymentLink,
      monthlyReservations,
      selected_reservation,
      loading,
    } = this.state;

    const title = new Date(selected_date).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
    const weekArray = getCurrentWeek(selected_date);

    const selectedTable = reservableTables.find(
      (t) => t.item._id === values.table,
    );

    const uniqueExperiences = this.getTableUniqueExperience(values.table);

    if (selected_reservation) {
      const originalExperience = selected_reservation.order_details.find(
        (od) => od.item?.type === "reservation_experience",
      )?.item;

      const originalTableId = selected_reservation.order_details.find(
        (od) => od.item?.type === "reservation",
      )?.item?._id;

      // Only add the original experience back if we are still on the same table
      if (originalExperience && values.table === originalTableId) {
        const isOriginalExperienceInList = uniqueExperiences.some(
          (exp) => exp._id === originalExperience._id,
        );
        if (!isOriginalExperienceInList) {
          uniqueExperiences.push(originalExperience);
        }
      }
    }

    const allTimeSlots = buildTimeSlots({
      reservableTables,
      allReservableTables,
    });

    let tablesForSelectedTime = values.reservation_time ? reservableTables : [];

    if (selected_reservation && values.reservation_time) {
      const originalTableId = selected_reservation.order_details.find(
        (od) => od.item?.type === "reservation",
      )?.item?._id;

      if (originalTableId) {
        const isOriginalTableInList = tablesForSelectedTime.some(
          (t) => t.item._id === originalTableId,
        );
        if (!isOriginalTableInList) {
          const originalTableFromAll = reservableTables.find(
            (t) => t.item._id === originalTableId,
          );
          if (originalTableFromAll)
            tablesForSelectedTime.push(originalTableFromAll);
        }
      }
    }

    const inputActions = {
      onChange: this.handleOnChange,
      onBlur: this.handleOnBlur,
    };

    const actions = {
      handleFeedbackError: this.handleFeedbackError,
      isTouched: this.isTouched,
    };

    return (
      <div
        className="flex-row reservations-calendar"
        style={{ maxHeight: 165, width: "100%" }}
      >
        <Card style={{ width: "100%" }}>
          <Card.Body className="p-4">
            <div className="d-flex flex-row">
              <div
                className="d-flex align-items-center cursor-pointer transition-all rounded-pill px-4 py-2 hover-bg-light shadow-xs border"
                onClick={this.handleShowDatepickerModal}
                style={{
                  backgroundColor: "#ffffff",
                  borderColor: `${RESERVATION_COLOR}33`,
                  minWidth: "200px",
                }}
              >
                <div className="symbol symbol-30 symbol-light-primary mr-3">
                  <span
                    className="symbol-label"
                    style={{ backgroundColor: `${RESERVATION_COLOR}15` }}
                  >
                    <i
                      className="far fa-calendar-alt fa-lg"
                      style={{ color: RESERVATION_COLOR }}
                    ></i>
                  </span>
                </div>
                <div className="d-flex flex-column">
                  <span
                    className="text-muted font-weight-boldest font-size-xs text-uppercase line-height-xs mb-1"
                    style={{ letterSpacing: "0.5px" }}
                  >
                    Select Service Date
                  </span>
                  <span className="font-pickup-lg font-weight-boldest text-dark-75">
                    {title}
                  </span>
                </div>
                <i className="fas fa-chevron-down ml-4 text-muted font-size-xs animate-bounce-vertical"></i>
              </div>
              <Button
                style={{
                  backgroundColor: RESERVATION_COLOR,
                  borderColor: RESERVATION_COLOR,
                }}
                className="ml-auto"
                onClick={() => {
                  this.handleShowModal();
                }}
              >
                Add Reservation
              </Button>
            </div>
          </Card.Body>
          <Card.Body className="p-1 px-4">
            <div className="d-flex justify-content-space-evenly align-items-center reservation-calendar pb-3">
              {/* Replaced SVG with text and styling */}
              <Button
                style={{
                  backgroundColor: RESERVATION_COLOR,
                  borderColor: RESERVATION_COLOR,
                  width: 35,
                  height: 35,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  fontWeight: "bold",
                }}
                className="svg-icon svg-icon-white p-1"
                onClick={() => onCalendarBtnClick(false)}
              >
                &lt;
              </Button>

              <div
                className="d-flex flex-row justify-content-space-around"
                style={{ width: "100%" }}
              >
                {weekArray.map(({ date, day, number }, index) => {
                  const isActive = index === 3;
                  return (
                    <div
                      // Use ISO string for a stable key
                      key={date.toISOString()}
                      onClick={() => onDayClick(date)}
                      style={{ cursor: "pointer" }}
                      className={`d-flex flex-column align-items-center font-weight-bolder p-2 ${
                        isActive ? "active-calendar" : ""
                      }`}
                    >
                      <div
                        className={`d-flex justify-content-center font-pickup-sm ${
                          isActive ? "text-white" : ""
                        }`}
                        style={{ height: 25, width: 30 }}
                      >
                        {day}
                      </div>
                      <div
                        className={`d-flex justify-content-center font-pickup-sm-2 p-1 ${
                          isActive ? "active-calendar-day" : ""
                        }`}
                        style={{ height: 30, width: 30 }}
                      >
                        {number}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Replaced SVG with text and styling */}
              <Button
                style={{
                  backgroundColor: RESERVATION_COLOR,
                  borderColor: RESERVATION_COLOR,
                  width: 35,
                  height: 35,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  fontWeight: "bold",
                }}
                className="svg-icon svg-icon-white p-1"
                onClick={() => onCalendarBtnClick(true)}
              >
                &gt;
              </Button>
            </div>
          </Card.Body>
        </Card>

        <Modal
          dialogClassName="modal-90w"
          size="xl"
          show={showDatepickerModal}
          onHide={this.handleCloseDatepickerModal}
          centered
        >
          {" "}
          <style>
            {`
              .modal-90w {
                max-width: 70vw !important;
              }
              .selected-reservation-date {
                border: 3px solid ${RESERVATION_COLOR} !important;
                font-weight: bold;
                border-radius: 4px; /* Optional: for rounded corners */
              }
            `}
          </style>
          <Modal.Header closeButton>
            <Modal.Title>Select a Date</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="events-calendar-wrapper">
              <FullCalendar
                className="events-calendar-content"
                ref={this.state.calendarRefOverview}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                initialDate={selected_date}
                events={this.fetchCalendarReservations}
                eventClick={(clickInfo) => {
                  const { onDateInputChange } = this.props;
                  const reservation = clickInfo.event.extendedProps.reservation;

                  onDateInputChange({
                    target: { value: reservation.order_date },
                  });

                  this.handleCloseDatepickerModal();
                  // Trigger the management modal directly
                  this.handleEventClick(clickInfo);
                }}
                dateClick={(arg) => {
                  arg.jsEvent.preventDefault();
                  onDateInputChange({
                    target: { value: arg.dateStr },
                  });
                  this.handleCloseDatepickerModal();
                }}
                height="auto"
                dayCellClassNames={(arg) => {
                  const cellDate = formatDateForInput(arg.date);
                  if (cellDate === formatDateForInput(selected_date)) {
                    return ["selected-reservation-date"];
                  }
                  return [];
                }}
                titleFormat={{
                  month: "long",
                  year: "numeric",
                }}
                dayMaxEvents={6}
                dayCellContent={this.dayHeaderContent}
              />
            </div>
            <style>
              {`
              .selected-reservation-date {
                border: 3px solid ${RESERVATION_COLOR} !important;
                font-weight: bold;
                border-radius: 4px; /* Optional: for rounded corners */
              }
            `}
            </style>
          </Modal.Body>
        </Modal>

        {/* --- NEW RESERVATION MODAL --- */}
        <Modal
          dialogClassName="modal-90w"
          show={showModal}
          size="xl"
          onHide={this.handleCloseModal}
          centered
        >
          {/* Injecting style tags to define the custom modal width
            and the new selected date class.
          */}
          <style>
            {`
              .modal-90w {
                max-width: 80vw !important;
              }
              .selected-reservation-date {
                background-color: ${RESERVATION_COLOR} !important;
                color: white !important;
                border: 3px solid ${RESERVATION_COLOR} !important;
                font-weight: bold;
                border-radius: 4px; /* Optional: for rounded corners */
              }
              .selected-reservation-date .fc-daygrid-day-number {
                color: white !important;
              }
              /* Theme FullCalendar Buttons */
              .fc .fc-button-primary {
                background-color: ${RESERVATION_COLOR} !important;
                border-color: ${RESERVATION_COLOR} !important;
                box-shadow: none !important;
                font-weight: 700 !important;
                text-transform: uppercase !important;
                font-size: 0.8rem !important;
                transition: all 0.2s ease !important;
              }
              .fc .fc-button-primary:hover {
                background-color: ${RESERVATION_COLOR}dd !important;
                border-color: ${RESERVATION_COLOR}dd !important;
                transform: translateY(-1px);
              }
              .fc .fc-button-primary:disabled {
                background-color: #ebedf3 !important;
                border-color: #ebedf3 !important;
                color: #b5b5c3 !important;
              }
              .fc .fc-toolbar-title {
                font-size: 1.25rem !important;
                font-weight: 800 !important;
                color: #3f4254 !important;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              /* Global Modal Button Overrides */
              .modal-content .btn-primary, 
              .modal-content .btn-info,
              .modal-content .btn-light-primary,
              .modal-content .btn-light-info {
                transition: all 0.3s ease !important;
              }
              .modal-content .btn-primary,
              .modal-content .btn-info {
                background-color: ${RESERVATION_COLOR} !important;
                border-color: ${RESERVATION_COLOR} !important;
              }
              .modal-content .btn-primary:hover,
              .modal-content .btn-info:hover {
                background-color: ${RESERVATION_COLOR}dd !important;
                border-color: ${RESERVATION_COLOR}dd !important;
              }
              .modal-content .btn-light-primary,
              .modal-content .btn-light-info {
                background-color: ${RESERVATION_COLOR}15 !important;
                color: ${RESERVATION_COLOR} !important;
                border-color: transparent !important;
              }
              .modal-content .btn-light-primary:hover,
              .modal-content .btn-light-info:hover {
                background-color: ${RESERVATION_COLOR}25 !important;
                color: ${RESERVATION_COLOR} !important;
              }
              .modal-content .text-primary,
              .modal-content .text-info {
                color: ${RESERVATION_COLOR} !important;
              }
              .modal-content .bg-primary,
              .modal-content .bg-info {
                background-color: ${RESERVATION_COLOR} !important;
              }
              .modal-content .bg-light-primary,
              .modal-content .bg-light-info {
                background-color: ${RESERVATION_COLOR}15 !important;
              }
              .modal-content .border-primary,
              .modal-content .border-info {
                border-color: ${RESERVATION_COLOR} !important;
              }
              .modal-content .badge-light-primary,
              .modal-content .badge-light-info {
                background-color: ${RESERVATION_COLOR}15 !important;
                color: ${RESERVATION_COLOR} !important;
              }
            `}
          </style>
          <Modal.Header closeButton className="border-0 px-10 pt-8 pb-0">
            <Modal.Title className="w-100">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <span
                    className={`btn btn-icon btn-sm btn-light-${
                      this.getModalMode() === "update"
                        ? "warning"
                        : this.getModalMode() === "delete"
                        ? "danger"
                        : "info"
                    } mr-4 no-hover`}
                    style={{ cursor: "default", width: "35px", height: "35px" }}
                  >
                    <i
                      className={`${
                        this.getModalMode() === "update"
                          ? "fas fa-edit"
                          : this.getModalMode() === "delete"
                          ? "fas fa-trash-alt"
                          : "fas fa-plus"
                      } font-size-sm`}
                    ></i>
                  </span>
                  <h3 className="font-weight-boldest text-dark mb-0 font-size-h4">
                    {this.getModalMode() === "update"
                      ? "Update Reservation"
                      : this.getModalMode() === "delete"
                      ? "Cancel Reservation"
                      : "Add Reservation"}
                  </h3>
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Container fluid>
              <SmartReservationForm
                values={values}
                isAdding={isAdding}
                isDeleting={isDeleting}
                loadingTables={loading}
                selected_reservation={selected_reservation}
                allTimeSlots={allTimeSlots}
                tablesForSelectedTime={tablesForSelectedTime}
                uniqueExperiences={uniqueExperiences}
                selectedTable={selectedTable}
                monthlyReservations={monthlyReservations}
                withPaymentLink={withPaymentLink}
                RESERVATION_COLOR={RESERVATION_COLOR}
                inputActions={inputActions}
                handlers={{
                  getTableUniqueExperience: this.getTableUniqueExperience,
                  handleDateChange: this.handleDateChange,
                  handlePartySizeChange: this.handlePartySizeChange,
                  handlePillClick: this.handlePillClick,
                  handleCreateReservation: this.handleCreateReservation,
                  handleCancelReservation: this.handleCancelReservation,
                  fetchCalendarReservations: this.fetchCalendarReservations,
                  formatDateForInput: formatDateForInput,
                  dayHeaderContent: this.dayHeaderContent,
                  handleReservationSelect: this.handleReservationSelect,
                  handleDietaryPillClick: this.handleDietaryPillClick,
                  handleUpdateDietaryCount: this.handleUpdateDietaryCount,
                  handleAddCustomDietaryItem: this.handleAddCustomDietaryItem,
                  handleCelebrationPillClick: this.handleCelebrationPillClick,
                  handleExclusiveExperiencePillClick:
                    this.handleExclusiveExperiencePillClick,
                  handleAddCustomCelebrationItem:
                    this.handleAddCustomCelebrationItem,
                  handlewithPaymentLinkChange: this.handlewithPaymentLinkChange,
                  handleResendEmail: this.handleResendEmail,
                  handleAddAdditional: this.handleAddAdditional,
                  handleRemoveAdditional: this.handleRemoveAdditional,
                }}
              />
            </Container>
          </Modal.Body>
        </Modal>
        {/* --- END NEW MODAL --- */}
      </div>
    );
  }
}

export default ReservationsCalendarHeader;
